import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, Briefcase, FileText, Wallet, Package,
  ScrollText, Settings, LogOut, Menu, X, Fingerprint, KeyRound, FileSpreadsheet, TrendingUp
} from "lucide-react";
import { useState } from "react";

const MENU = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: "*" },
  { to: "/absen", label: "Absensi", icon: Fingerprint, roles: "*" },
  { to: "/crm", label: "CRM", icon: Users, roles: ["Owner","Sales","Finance Admin"] },
  { to: "/projects", label: "Project", icon: Briefcase, roles: "*" },
  { to: "/quotations", label: "Quotation", icon: FileText, roles: ["Owner","Sales","Finance Admin"] },
  { to: "/finance", label: "Keuangan", icon: Wallet, roles: ["Owner","Finance Admin"] },
  { to: "/laba", label: "Laba Project", icon: TrendingUp, roles: ["Owner","Finance Admin"] },
  { to: "/pettycash", label: "Petty Cash", icon: Wallet, roles: ["Owner","Finance Admin","Produksi"] },
  { to: "/inventory", label: "Inventory", icon: Package, roles: ["Owner","Produksi","Creative"] },
  { to: "/masters", label: "Master Data", icon: ScrollText, roles: ["Owner","Finance Admin","Sales"] },
  { to: "/sheets", label: "Impor Sheets", icon: FileSpreadsheet, roles: ["Owner","Finance Admin"] },
  { to: "/audit", label: "Audit Log", icon: ScrollText, roles: ["Owner"] },
  { to: "/settings", label: "Pengaturan", icon: Settings, roles: ["Owner"] },
];

const BOTTOM_NAV = [
  { to: "/", label: "Beranda", icon: LayoutDashboard, roles: "*" },
  { to: "/absen", label: "Absen", icon: Fingerprint, roles: "*" },
  { to: "/crm", label: "CRM", icon: Users, roles: ["Owner","Sales","Finance Admin"] },
  { to: "/projects", label: "Project", icon: Briefcase, roles: "*" },
  { to: "/finance", label: "Keuangan", icon: Wallet, roles: ["Owner","Finance Admin"] },
  { to: "/pettycash", label: "Kas", icon: Wallet, roles: ["Produksi"] },
  { to: "/inventory", label: "Inventory", icon: Package, roles: ["Produksi","Creative"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const canSee = (roles) => roles === "*" || user?.role === "Owner" || roles.includes(user?.role);

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-[#E2DDD3]">
        <div className="flex items-center justify-between px-4 py-3 lg:px-8">
          <div className="flex items-center gap-3">
            <button onClick={() => setOpen(!open)} className="lg:hidden p-2 -ml-2" data-testid="menu-toggle">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="Puade" className="h-8 w-auto" data-testid="header-logo" />
              <div className="hidden sm:block border-l border-[#E2DDD3] pl-2 text-[10px] uppercase tracking-wider text-[#B87333] leading-tight">OS<br />Internal</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <div className="text-sm font-medium text-[#1A2627]" data-testid="header-user-name">{user?.name}</div>
              <div className="text-[11px] text-[#8A999A]">{user?.role}</div>
            </div>
            <span className="chip bg-[#0F5257] text-white" data-testid="header-user-role">{user?.role}</span>
            <Link to="/akun" data-testid="account-link" className="p-2 rounded-lg hover:bg-[#F2EEE7]" title="Akun & password"><KeyRound size={18} /></Link>
            <button onClick={() => { logout(); nav("/login"); }} data-testid="logout-button"
              className="p-2 rounded-lg hover:bg-[#F2EEE7]"><LogOut size={18} /></button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar desktop */}
        <aside className="hidden lg:block w-60 shrink-0 border-r border-[#E2DDD3] bg-white min-h-[calc(100vh-61px)] p-4">
          <nav className="space-y-1">
            {MENU.filter(m => canSee(m.roles)).map(m => {
              const Icon = m.icon;
              const active = loc.pathname === m.to || (m.to !== "/" && loc.pathname.startsWith(m.to));
              return (
                <Link key={m.to} to={m.to} data-testid={`nav-${m.label.toLowerCase().replace(" ","-")}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition
                    ${active ? "bg-[#0F5257] text-white" : "text-[#1A2627] hover:bg-[#F2EEE7]"}`}>
                  <Icon size={18} />{m.label}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Mobile drawer */}
        {open && (
          <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setOpen(false)}>
            <div className="absolute inset-0 bg-black/40"></div>
            <nav className="relative bg-white w-72 h-full p-4 overflow-y-auto">
              {MENU.filter(m => canSee(m.roles)).map(m => {
                const Icon = m.icon;
                const active = loc.pathname === m.to;
                return (
                  <Link key={m.to} to={m.to} onClick={() => setOpen(false)}
                    className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm
                      ${active ? "bg-[#0F5257] text-white" : "text-[#1A2627] hover:bg-[#F2EEE7]"}`}>
                    <Icon size={18} />{m.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        )}

        <main className="flex-1 min-w-0 pb-20 lg:pb-6">{children}</main>
      </div>

      {/* Bottom nav mobile */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur-md border-t border-[#E2DDD3]">
        <div className="flex">
          {BOTTOM_NAV.filter(m => canSee(m.roles)).slice(0, 4).map(m => {
            const Icon = m.icon;
            const active = loc.pathname === m.to;
            return (
              <Link key={m.to} to={m.to} data-testid={`bottom-nav-${m.label.toLowerCase()}`}
                className={`flex-1 flex flex-col items-center justify-center py-2 gap-1 text-[11px]
                  ${active ? "text-[#0F5257]" : "text-[#8A999A]"}`}>
                <Icon size={20} />{m.label}
              </Link>
            );
          })}
          <button onClick={() => setOpen(true)} data-testid="bottom-nav-more"
            className="flex-1 flex flex-col items-center justify-center py-2 gap-1 text-[11px] text-[#8A999A]">
            <Menu size={20} />Lainnya
          </button>
        </div>
      </nav>
    </div>
  );
}
